import { useState, useEffect, useRef } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './Button';

interface InlineConfirmationProps {
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  autoCloseMs?: number;
  isLoading?: boolean;
  loadingText?: string;
  variant?: 'default' | 'danger' | 'success';
}

export function InlineConfirmation({
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  autoCloseMs = 5000,
  isLoading = false,
  loadingText,
  variant = 'default',
}: InlineConfirmationProps) {
  const [timeLeft, setTimeLeft] = useState(autoCloseMs / 1000);
  const confirmRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Scroll into view when component mounts
    if (confirmRef.current) {
      confirmRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    const timer = setTimeout(() => {
      onCancel();
    }, autoCloseMs);

    const interval = setInterval(() => {
      setTimeLeft((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [autoCloseMs, onCancel]);

  return (
    <div ref={confirmRef} className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 sm:p-4 space-y-3 w-full max-w-full overflow-hidden">
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-500 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-yellow-900 dark:text-yellow-200">
            {message}
          </p>
          <p className="text-xs text-yellow-700 dark:text-yellow-400 mt-1">
            Auto-cancels in {timeLeft}s
          </p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 justify-end">
        <Button
          onClick={onCancel}
          variant="outline"
          size="sm"
          className="border-gray-300 dark:border-gray-600 w-full sm:w-auto"
          disabled={isLoading}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          size="sm"
          className={
            variant === 'success'
              ? 'bg-green-600 hover:bg-green-700 text-white w-full sm:w-auto'
              : variant === 'danger'
                ? 'bg-red-600 hover:bg-red-700 text-white w-full sm:w-auto'
                : 'bg-blue-600 hover:bg-blue-700 text-white w-full sm:w-auto'
          }
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              {loadingText || confirmText}
            </>
          ) : (
            confirmText
          )}
        </Button>
      </div>
    </div>
  );
}


